import { createColumnHelper } from "@tanstack/react-table";
import ItemGrid from "../shared/ItemGrid";
import {
  FolderOpenIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import { useConfigurator, useProject, useUser } from "../../hooks/useQueries";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-modal-global";
import PopupModal from "../shared/Modal";
import Input from "../shared/Input";
import { find, round } from "lodash";
import { useState, useEffect, useRef } from "react";
import axios from "../../axios";
import { generalStore } from "../../stores/userStore";
import { useNav } from "../../hooks/useQueries";
import { toast } from "react-toastify";
import PriceAddOnModal from "./PriceAddOnModal";
export default function ProjectItemsGrid({ items, totalQuantity, totalPrice, discountLimit, user, priceBreakdown, project, updateProjectAddons, refreshProjectPrice }) {
  const { updateCurrentNavigation } = useNav();
  const [hideLoading, setHideLoading] = generalStore((state) => [
    state.hideLoading,
    state.setHideLoading,
  ]);

  const { selectProjectVentilationUnit } = useConfigurator();
  const { deleteProjectItem, cloneProjectItem, updateProjectItemField } = useProject();
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const canSeeDiscount = user?.userGroupId == 2 || (user?.showSalePrice ?? false);
  const navigate = useNavigate();

  const columnHelper = createColumnHelper();

  const fmtDecimal = (val) => {
    if (val === undefined || val === null) {
      return "Enquire";
    }
    if (val === -1) {
      return "Enquire";
    }
    return val.toFixed(2);
  };
  const Row = ({ label, value, className = "" }) => (
    <tr className={className}>
      <td className="px-4 py-2 text-sm text-gray-700">{label}</td>
      <td className="px-4 py-2 text-sm text-gray-900 text-right font-medium">{value}</td>
    </tr>
  );

  const openAddOnsModal = () => {
    setIsServiceModalOpen(true);
  };

  const handleUpdateProjectAddons = async (addonData) => {
    await updateProjectAddons(addonData);
  };

  const [vatValue, setVatValue] = useState(priceBreakdown?.vatPercent ?? 0);
  const vatTimeoutRef = useRef(null);

  useEffect(() => {
    setVatValue(priceBreakdown?.vatPercent ?? 0);
  }, [priceBreakdown?.vatPercent]);

  const handleVatChange = (value) => {
    let newValue = parseInt(value) || 0;

    if (newValue < 0) {
      newValue = 0;
    }

    setVatValue(newValue);

    if (vatTimeoutRef.current) {
      clearTimeout(vatTimeoutRef.current);
    }

    vatTimeoutRef.current = setTimeout(async () => {
      await updateProjectAddons({
        vat: newValue
      });
      if (refreshProjectPrice) {
        refreshProjectPrice();
      }
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (vatTimeoutRef.current) {
        clearTimeout(vatTimeoutRef.current);
      }
    };
  }, []);

  const totalWithAddons = (() => {
    if (!priceBreakdown || priceBreakdown.isEnquire) return -1;
    return (priceBreakdown.discountedExFactoryPrice ?? 0)
      + (priceBreakdown.dealerMarginAmount ?? 0)
      + (priceBreakdown.warrantyAmount ?? 0)
      + (priceBreakdown.packingAmount ?? 0)
      + (priceBreakdown.servicesAmount ?? 0)
      + (priceBreakdown.fatAmount ?? 0)
      + (priceBreakdown.cfdCarParkAmount ?? 0)
      + (priceBreakdown.cfdThermalAmount ?? 0);
  })();

  const Cell = (getValue, rowIndex, columnId, table, guid, inputType) => {
    // const initialValue = getValue();

    var resultItem = find(items, (i) => i.guid == guid);
    const initialValue = resultItem ? resultItem[columnId] : getValue();

    // We need to keep and update the state of the cell normally
    const [value, setValue] = useState();

    // if (resultItem) {
    //   setValue(resultItem[columnId]);
    // }

    const onBlur = async () => {
      // Ensure discount doesn't go below 0
      let finalValue = value;
      if (columnId === "lineItemDiscount" || columnId === "quantity") {
        const numVal = parseFloat(value) || 0;
        finalValue = Math.max(0, numVal);
      }

      table.options.meta?.updateData(rowIndex, columnId, finalValue);
      var resultItem = find(items, (i) => i.guid == guid);

      if (resultItem) {
        // Validate discount limit
        if (columnId === "lineItemDiscount") {
          const numVal = parseFloat(finalValue) || 0;
          if (discountLimit !== null && discountLimit !== undefined && numVal > discountLimit) {
            toast.error(`Discount cannot exceed ${discountLimit}%`);
            setValue(initialValue);
            return;
          }
        }

        resultItem[columnId] = finalValue;

        await updateProjectItemField({
          guid: resultItem.guid,
          quantity: resultItem.quantity,
          reference: resultItem.reference,
          lineItemDiscount: parseFloat(resultItem.lineItemDiscount) || 0,
        });

        if (columnId == "quantity" || columnId == "lineItemDiscount") {
          setHideLoading(false);
          const { data } = await axios.get(
            `/Project/get-project-result-item/${guid}`
          );
          table.options.meta?.updateData(rowIndex, "priceText", data.priceText);
          table.options.meta?.updateData(rowIndex, "totalPriceText", data.totalPriceText);
          setHideLoading(true);
        }
        if (refreshProjectPrice) {
          await refreshProjectPrice();
        }
      }
    };

    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    return (
      <Input
        elementtype="input"
        name="reference"
        value={value}
        type={inputType ? inputType : "text"}
        onChange={(e) => setValue(e.target.value)}
        onBlur={onBlur}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      />
    );
  };

  // const updateData = async (item) => {
  //   return await axios.get(`/Project/get-project-item/${item.guid}`);
  // };

  const columns = [
    columnHelper.accessor("", {
      id: "srNo",
      cell: (info) => info.row.index + 1,
      header: "Sr No.",
    }),
    columnHelper.accessor("description", {
      id: "description",
      header: "Model",
    }),
    columnHelper.accessor("devType", {
      id: "devType",
      header: () => <span className="whitespace-nowrap text-center">DEV Type</span>,
      cell: (info) => <div className="text-center">{info.getValue() ?? ""}</div>,
    }),
    columnHelper.accessor("reference", {
      id: "reference",
      cell: ({ getValue, row, column: { id }, table }) => {
        return Cell(getValue, row.index, id, table, row.original.guid);
      },
      header: "Reference",
    }),
    columnHelper.accessor("volume", {
      cell: (info) =>
        info.row.original.isCustom
          ? ""
          : info.getValue() + ` (${info.row.original.volumeUnit})`,
      header: (props) => "Volume",
    }),
    columnHelper.accessor("externalStaticPressure", {
      cell: (info) =>
        info.row.original.isCustom
          ? ""
          : info.getValue() + ` (${info.row.original.pressureUnit})`,
      header: (props) => "ESP",
    }),
    columnHelper.accessor("priceText", {
      id: "priceText",
      header: "Price (AED)",
      // columnVisibility:
      // cell: (info) =>
      //   info.getValue() == -1 ? "Enquire" : round(info.renderValue()),
    }),
    columnHelper.accessor("quantity", {
      header: "Quantity",
      cell: ({ getValue, row, column: { id }, table }) => {
        return Cell(
          getValue,
          row.index,
          id,
          table,
          row.original.guid,
          "number"
        );
      },
    }),
    columnHelper.accessor("lineItemDiscount", {
      id: "lineItemDiscount",
      header: "Discount (%)",
      cell: ({ getValue, row, column: { id }, table }) =>
        Cell(getValue, row.index, id, table, row.original.guid, "number"),
    }),
    columnHelper.accessor("totalPriceText", {
      id: "totalPriceText",
      header: "Total Price (AED)",
      // cell: (info) =>
      //   info.getValue() == -1 ? "Enquire" : round(info.renderValue()),
    }),
    columnHelper.accessor("", {
      id: "cloneItem",
      cell: (props) => (
        <DocumentDuplicateIcon
          className="h-5 w-5 text-green-600 cursor-pointer"
          onClick={() => onCloneProjectItem(props?.row?.original?.guid)}
        />
      ),
    }),
    columnHelper.accessor("", {
      id: "openUnit",
      cell: (props) => (
        <FolderOpenIcon
          className="h-5 w-5 text-green-600 cursor-pointer"
          onClick={() => onOpenProjectItem(props?.row?.original?.guid)}
        />
      ),
    }),
    columnHelper.accessor("", {
      id: "deleteUnit",
      cell: (props) => (
        <TrashIcon
          className="h-5 w-5 text-red-600 cursor-pointer"
          onClick={() => onDeleteProjectItem(props?.row?.original?.guid)}
        />
      ),
    }),
  ];

  const [columnVisibility, setColumnVisibility] = useState({
    priceText: user.userGroupId == 2 || (user.showSalePrice ?? false),
    totalPriceText: user.userGroupId == 2 || (user.showSalePrice ?? false),
    lineItemDiscount: canSeeDiscount,
  });

  function onCloneProjectItem(id) {
    cloneProjectItem({ resultId: id });
  }

  function onOpenProjectItem(id) {
    selectProjectVentilationUnit({ ventilationUnitId: id });
    navigate("/selection");
    //updateCurrentNavigation("Selection")
  }

  function onDeleteProjectItem(id) {
    Modal.open(PopupModal, {
      title: "",
      weak: true,
      body: ["Are you sure you want to delete this unit?"],
      callback: () => {
        deleteProjectItem({ resultId: id });
      },
      closable: false,
    });
  }

  const [sorting, setSorting] = useState([
    {
      id: "createdDate",
      desc: false, // sort by name in descending order by default
    },
  ]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [columnFilters, setColumnFilters] = useState(); // const [pagination, setPagination] = useState({

  return (
    <>
      <ItemGrid
        columns={columns}
        items={items}
        //columnVisibility={columnVisibility}
        tableOptions={{
          enableColumnFilters: false,
          state: {
            columnVisibility,
          },
          // onSortingChange: setSorting,
          // onPaginationChange: setPagination,
          // onColumnFiltersChange: setColumnFilters,
          // getFilteredRowModel: getFilteredRowModel(), //client side filtering
        }}
      />

      {items && items.length > 0 && (user.userGroupId == 2 || (user.showSalePrice ?? false)) && (
        <div className="border-y border-gray-200 py-2 px-6">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">VAT:</span>
              <div className="relative">
                <div className="relative">
                  <Input
                    elementtype="input"
                    type="number"
                    placeholder="0"
                    step="1"
                    min="0"
                    value={vatValue}
                    onChange={(e) => handleVatChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === '.' || e.key === ',') {
                        e.preventDefault();
                      }
                    }}
                    className="w-24 text-center border-2 border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm font-semibold bg-gray-50 text-gray-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 
                    focus:bg-white focus:outline-none transition-all duration-200 hover:border-blue-400 hover:bg-white hover:shadow-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none
                    [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-500 pointer-events-none select-none">
                    %
                  </span>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="px-4 py-1.5 text-sm font-medium text-blue-600 bg-transparent border-2 border-blue-600 rounded-lg hover:bg-blue-50 hover:border-blue-700 hover:text-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all duration-200"
              onClick={openAddOnsModal}
            >
              Svc. and Freight
            </button>
          </div>

          <div className="border border-gray-300 rounded-md overflow-hidden mt-2">
            <table className="w-full">
              <tbody>
                <Row
                  label="Total Quantity"
                  value={totalQuantity}
                  className="border-b border-gray-200 last:border-b-0"
                />

                <Row
                  label={`Ex-Factory Price (${priceBreakdown?.currency ?? "AED"})`}
                  value={fmtDecimal(priceBreakdown?.exFactoryPrice)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  className="border-b border-gray-200 last:border-b-0"
                />

                <Row
                  label={`Factory Discount${priceBreakdown?.discountPercent > 0 ? ` ${priceBreakdown.discountPercent}%` : ""}${priceBreakdown?.italyDiscountPercent > 0 ? ` + Italy ${priceBreakdown.italyDiscountPercent}%` : ""} (${priceBreakdown?.currency ?? "AED"})`}
                  value={fmtDecimal(priceBreakdown?.factoryDiscountAmount)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  className="border-b border-gray-200 last:border-b-0"
                />

                <Row
                  label={`Discounted Ex-Factory Price (${priceBreakdown?.currency ?? "AED"})`}
                  value={fmtDecimal(priceBreakdown?.discountedExFactoryPrice)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  className="border-b border-gray-200 last:border-b-0 font-semibold"
                />

                {priceBreakdown?.dealerMarginPercent > 0 && (
                  <Row
                    label={`Sales Office Margin ${priceBreakdown.dealerMarginPercent}% (${priceBreakdown.currency})`}
                    value={fmtDecimal(priceBreakdown?.dealerMarginAmount)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    className="border-b border-gray-200 last:border-b-0"
                  />
                )}

                {priceBreakdown?.warrantyMultiplier !== 1 && (
                  <Row
                    label={`Warranty (${priceBreakdown?.currency})`}
                    value={fmtDecimal(priceBreakdown?.warrantyAmount)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    className="border-b border-gray-200 last:border-b-0"
                  />
                )}

                {priceBreakdown?.packingMultiplier !== 1 && (
                  <Row
                    label={`Packing (${priceBreakdown?.currency})`}
                    value={fmtDecimal(priceBreakdown?.packingAmount)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    className="border-b border-gray-200 last:border-b-0"
                  />
                )}

                {priceBreakdown?.servicesAmount > 0 && (
                  <Row
                    label={`Services (${priceBreakdown.currency})`}
                    value={fmtDecimal(priceBreakdown.servicesAmount)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    className="border-b border-gray-200 last:border-b-0"
                  />
                )}

                {priceBreakdown?.fatAmount > 0 && (
                  <Row
                    label={`FAT – Air & Sound Performance (${priceBreakdown.currency})`}
                    value={fmtDecimal(priceBreakdown.fatAmount)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    className="border-b border-gray-200 last:border-b-0"
                  />
                )}

                {priceBreakdown?.cfdCarParkAmount > 0 && (
                  <Row
                    label={`CFD – Carpark Design & Services (${priceBreakdown.currency})`}
                    value={fmtDecimal(priceBreakdown.cfdCarParkAmount)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    className="border-b border-gray-200 last:border-b-0"
                  />
                )}

                {priceBreakdown?.cfdThermalAmount > 0 && (
                  <Row
                    label={`CFD – Thermal Analysis Services (${priceBreakdown.currency})`}
                    value={fmtDecimal(priceBreakdown.cfdThermalAmount)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    className="border-b border-gray-200 last:border-b-0"
                  />
                )}

                {priceBreakdown?.freightAmount > 0 && (
                  <>
                    <Row
                      label={`Freight (${priceBreakdown.currency})`}
                      value={fmtDecimal(priceBreakdown.freightAmount)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      className="border-b border-gray-200 last:border-b-0"
                    />
                    <Row
                      label={priceBreakdown.incoterm === 33
                        ? `Total FOB (${priceBreakdown.currency})`
                        : `Total C & F (${priceBreakdown.currency})`}
                      value={fmtDecimal(priceBreakdown.totalCandF)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      className="border-b border-gray-200 last:border-b-0 font-semibold"
                    />
                  </>
                )}

                {priceBreakdown?.dutyPercent > 0 && (
                  <>
                    <Row
                      label={`Duty & Clearance ${priceBreakdown.dutyPercent}% (${priceBreakdown.currency})`}
                      value={fmtDecimal(priceBreakdown.dutyAmount)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      className="border-b border-gray-200 last:border-b-0"
                    />
                    <Row
                      label={`Total DDP (${priceBreakdown.currency})`}
                      value={fmtDecimal(priceBreakdown.totalDDP)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      className="border-b border-gray-200 last:border-b-0 font-semibold"
                    />
                  </>
                )}

                {priceBreakdown?.vatEnabled ? (
                  <>
                    <Row label={`VAT ${priceBreakdown.vatPercent}% (${priceBreakdown.currency})`} value={fmtDecimal(priceBreakdown.vatCharge)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} className="border-b border-gray-200 last:border-b-0" />
                    <Row label={`Total Price inc. VAT (${priceBreakdown.currency})`} value={fmtDecimal(priceBreakdown.totalIncVAT)?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} className="border-t-2 border-blue-500 font-bold text-blue-600" />
                  </>
                ) : (
                  <Row
                    label={`Total Price (${priceBreakdown?.currency ?? "AED"})`}
                    value={fmtDecimal(
                      priceBreakdown?.totalDDP > 0
                        ? priceBreakdown.totalDDP
                        : priceBreakdown?.totalCandF > 0
                          ? priceBreakdown.totalCandF
                          : totalWithAddons
                    )?.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    className="border-t-2 border-blue-500 font-bold text-blue-600"
                  />
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PriceAddOnModal
        isOpen={isServiceModalOpen}
        onClose={() => setIsServiceModalOpen(false)}
        project={project}
        updateProjectAddons={handleUpdateProjectAddons}
        refreshProjectPrice={refreshProjectPrice}
      />
    </>
  );
}